import { useTranslation } from 'react-i18next';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { SideTooltip } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useChatUnreadCountQuery } from '@/hooks/useChat';
import { usePendingIntakeCountQuery } from '@/hooks/useIntake';
import { useSupportUnreadCountQuery } from '@/hooks/useSupport';
import { buildNameInitials } from '@/lib/nameInitials';
import { cn } from '@/lib/utils';
import { selectVisibleNavigationItems } from './navigationItems';
import { WorkspaceBadge } from './WorkspaceBadge';
import { ElevatedWorkspaceHeader } from './ElevatedWorkspaceHeader';
import { CollapsedBrandMark } from './CollapsedBrandMark';
import { PractitionerSidebarItem } from './PractitionerSidebarItem';
import { useSidebarCollapsed } from './useSidebarCollapsed';
import { WorkspaceSwitcher } from '@/components/layout/WorkspaceSwitcher';

export function PractitionerSidebar() {
  const { t } = useTranslation(['onboarding', 'common', 'chat', 'support', 'intake', 'navigation']);
  const { currentUser, activeWorkspace, enabledModules, isElevatedWorkspaceAccess, logOut } =
    useAuth();
  const { isCollapsed, toggleCollapsed } = useSidebarCollapsed();
  const isOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const displayName = currentUser === null ? '' : currentUser.fullName;
  const chatUnreadCountQuery = useChatUnreadCountQuery('practitioner');
  const chatUnreadCount = chatUnreadCountQuery.data === undefined ? 0 : chatUnreadCountQuery.data;
  const supportUnreadCountQuery = useSupportUnreadCountQuery('practitioner');
  const supportUnreadCount =
    supportUnreadCountQuery.data === undefined ? 0 : supportUnreadCountQuery.data;

  const pendingIntakeCountQuery = usePendingIntakeCountQuery(
    enabledModules.includes('first_consultation')
  );
  const pendingIntakeCount =
    pendingIntakeCountQuery.data === undefined ? 0 : pendingIntakeCountQuery.data;

  function resolveUnreadCount(itemId: string): number {
    if (itemId === 'support') {
      return supportUnreadCount;
    }
    if (itemId === 'intake') {
      return pendingIntakeCount;
    }
    return chatUnreadCount;
  }

  function resolveBadgeAriaLabel(itemId: string, count: number): string {
    if (itemId === 'intake') {
      return t('intake:inbox.pendingBadgeAriaLabel', { count });
    }
    return t('chat:nav.unreadBadgeAriaLabel', { count });
  }

  const roleLabel =
    isElevatedWorkspaceAccess === true
      ? t('navigation:workspaceSwitcher.platform')
      : isOwner === true
        ? t('onboarding:role.owner')
        : t('onboarding:role.practitioner');
  const visibleItems = selectVisibleNavigationItems(enabledModules, isOwner);
  const toggleLabel = isCollapsed
    ? t('navigation:sidebar.expand')
    : t('navigation:sidebar.collapse');

  const collapseToggle = (
    <Button
      variant="ghost"
      size="icon"
      aria-label={toggleLabel}
      aria-expanded={isCollapsed === false}
      title={toggleLabel}
      onClick={toggleCollapsed}
    >
      <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} />
    </Button>
  );

  return (
    <aside
      data-collapsed={isCollapsed}
      className={cn(
        'hidden lg:flex bg-zoovpalelavender border-r border-zooborder flex-col shrink-0 h-full overflow-hidden transition-[width] duration-200',
        isCollapsed ? 'w-[76px]' : 'w-60'
      )}
    >
      {isCollapsed ? (
        <div className="py-4 border-b border-zooborder flex flex-col items-center gap-2">
          <CollapsedBrandMark />
          {collapseToggle}
        </div>
      ) : (
        <div className="p-5 pb-3 border-b border-zooborder">
          <div className="flex items-start justify-between gap-2 mb-2">
            <BrandLogo className="h-8 max-w-full w-auto object-contain" />
            <div className="-mt-1 -mr-2 shrink-0">{collapseToggle}</div>
          </div>
          {isElevatedWorkspaceAccess === true ? (
            <ElevatedWorkspaceHeader />
          ) : (
            <>
              <WorkspaceSwitcher tone="light" />
              <div className="mt-1.5 px-2">
                <WorkspaceBadge
                  tenantType={activeWorkspace === null ? null : activeWorkspace.tenantType}
                />
              </div>
            </>
          )}
        </div>
      )}

      {isCollapsed ? (
        <div className="py-4 flex justify-center">
          <SideTooltip label={displayName + ' · ' + roleLabel}>
            <div
              role="img"
              aria-label={displayName + ', ' + roleLabel}
              className="w-10 h-10 rounded-full bg-zooprimary text-zoowhite flex items-center justify-center font-bold text-xs"
            >
              {buildNameInitials(displayName)}
            </div>
          </SideTooltip>
        </div>
      ) : (
        <div className="px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-zooprimary text-zoowhite flex items-center justify-center font-bold text-xs shrink-0">
            {buildNameInitials(displayName)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zooindigo truncate">{displayName}</div>
            <div className="text-xs text-zoomuted flex items-center gap-1">
              {isElevatedWorkspaceAccess !== true && isOwner === true ? (
                <Icon name="Crown" className="text-zoowarningtext" />
              ) : null}
              {roleLabel}
            </div>
          </div>
        </div>
      )}

      <nav
        className={cn(
          'flex-1 min-h-0 overflow-y-auto py-2 space-y-1 text-sm',
          isCollapsed ? 'px-3' : 'px-4'
        )}
      >
        {visibleItems.map(function renderItem(item) {
          const unreadCount = resolveUnreadCount(item.id);
          return (
            <PractitionerSidebarItem
              key={item.id}
              item={item}
              isCollapsed={isCollapsed}
              unreadCount={unreadCount}
              unreadBadgeAriaLabel={resolveBadgeAriaLabel(item.id, unreadCount)}
            />
          );
        })}
      </nav>

      <div
        className={cn(
          'border-t border-zooborder flex items-center gap-2',
          isCollapsed ? 'flex-col py-3' : 'px-4 py-3 justify-between'
        )}
      >
        <LanguageSwitcher className={isCollapsed ? 'flex-col' : undefined} />
        <SideTooltip isEnabled={isCollapsed} label={t('common:actions.logout')}>
          <Button
            variant="destructiveGhost"
            size="icon"
            aria-label={t('common:actions.logout')}
            title={isCollapsed ? undefined : t('common:actions.logout')}
            onClick={function handleLogout() {
              logOut();
            }}
          >
            <Icon name="LogOut" />
          </Button>
        </SideTooltip>
      </div>
    </aside>
  );
}
